/**
 * Acquisition and storage of the per-server cloud access token (CL-18307).
 *
 * Passport talks to several regional VP5 API hosts. For each one it holds a
 * bearer token in localStorage under the server's bare hostname. Before this
 * module that token was only ever fetched REACTIVELY, from ClientApi's 401
 * response interceptor, which produced the request-ordering defect reported on
 * CL-18307:
 *
 *   keystroke -> availability request with no Authorization header
 *             -> 401 Unauthenticated           (1st console error)
 *             -> get_client_secret_for_passport (extra request)
 *             -> access_token                   (extra request)
 *             -> token stored, original request NEVER retried
 *
 * So the first probe of a typing burst always failed, three requests went out
 * where one was wanted, and the answer the user was waiting for never arrived
 * until they typed another character. The reporter asked why the calls are
 * rejected before the token is ready: they are rejected because the client
 * sends them before it has a token. The server is behaving correctly -- an
 * unauthenticated request to a `client`-middleware route is supposed to 401.
 * The ordering is the client's to fix, and `ensureToken` below is the fix.
 *
 * Two further failure modes handled here, both of which showed up as console
 * noise on the same ticket:
 *
 *   * The old 401 handler did `localStorage.setItem(name, JSON.stringify(t))`
 *     with `t === undefined` whenever the secret or token call failed.
 *     `JSON.stringify(undefined)` is the JS value `undefined`, which setItem
 *     coerces to the STRING "undefined" -- and `JSON.parse("undefined")` then
 *     throws a SyntaxError on every subsequent read, permanently, until the
 *     key is cleared by hand. `writeToken` removes the key instead of writing
 *     a poisoned value, and `readToken` treats an unparseable value as absent.
 *
 *   * Nothing de-duplicated concurrent acquisition, so N requests racing with
 *     no token produced N secret calls and N token calls, each overwriting the
 *     last. `ensureToken` is single-flight per server.
 */

import UserService from "./userService";

/**
 * In-flight acquisition per server, keyed by server_api. Module scope on
 * purpose: ClientApi builds a NEW axios instance on every call, so anything
 * held on the instance would de-duplicate nothing.
 *
 * @type {Object<string, Promise<?string>>}
 */
let inFlight = {};

/**
 * The localStorage key for a server: its hostname with any scheme stripped.
 *
 * Extracted because ClientApi derived it inline in three places with three
 * copies of the same two replaces, and a token written under one spelling and
 * read under another is indistinguishable from having no token at all.
 *
 * @param {{server_name: string}} server
 * @returns {string}
 */
export function serverStorageKey(server) {
  let name = (server && server.server_name) || "";
  return name.replace(/^https?:\/\//, "");
}

/**
 * The stored token for a server, or null when there is none usable.
 *
 * Never throws: a corrupt entry (see "undefined" above) reads as absent, which
 * lets the caller recover by acquiring a fresh token instead of breaking the
 * request interceptor for the rest of the session.
 *
 * @param {{server_name: string}} server
 * @returns {?string}
 */
export function readToken(server) {
  const key = serverStorageKey(server);
  if (!key) {
    return null;
  }

  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch (e) {
    // Storage disabled (private mode, blocked cookies). Treated as no token.
    return null;
  }

  if (raw === null) {
    // Nothing stored. Normal, and nothing to clean up.
    return null;
  }

  // Values an older build could leave behind. "undefined" in particular is
  // what `JSON.stringify(undefined)` becomes once setItem coerces it to a
  // string, and it makes JSON.parse throw on every subsequent read. Remove it
  // rather than merely reporting it as absent, so the key stops being a
  // landmine for any other code that reads it directly.
  if (raw === "" || raw === "undefined" || raw === "null") {
    clearToken(server);
    return null;
  }

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    // Poisoned by an older build. Drop it so it stops throwing on every read.
    clearToken(server);
    return null;
  }

  return typeof parsed === "string" && parsed !== "" ? parsed : null;
}

/**
 * Store a token, or clear the key when the value is not a usable token.
 *
 * @param {{server_name: string}} server
 * @param {*} token
 * @returns {?string} What is now stored, or null if the key was cleared.
 */
export function writeToken(server, token) {
  const key = serverStorageKey(server);
  if (!key) {
    return null;
  }

  if (typeof token !== "string" || token === "") {
    clearToken(server);
    return null;
  }

  try {
    localStorage.setItem(key, JSON.stringify(token));
  } catch (e) {
    return null;
  }

  return token;
}

/**
 * @param {{server_name: string}} server
 */
export function clearToken(server) {
  const key = serverStorageKey(server);
  if (!key) {
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch (e) {
    // Nothing to do: an unwritable store cannot hold a stale token either.
  }
}

/**
 * Fetch a fresh token for a server. Single-flight: concurrent callers share
 * one secret call and one access_token call.
 *
 * Resolves null rather than rejecting when acquisition fails. The caller's job
 * is to send the request either way -- a request without a token gets a clean
 * 401 that the response interceptor already understands, which is a far better
 * outcome than an exception thrown from inside a request interceptor (that
 * surfaces as a rejected request with no `error.response` at all, which is
 * exactly the undefined-response shape whose unguarded handlers CL-18307 is
 * reporting).
 *
 * @param {{server_name: string, server_api: string}} server
 * @param {{userService?: Object}} [deps] Injection seam for tests.
 * @returns {Promise<?string>}
 */
export function acquireToken(server, deps) {
  const userService = (deps && deps.userService) || UserService;
  const api = (server && server.server_api) || "";

  if (!api) {
    return Promise.resolve(null);
  }

  if (inFlight[api]) {
    return inFlight[api];
  }

  const attempt = Promise.resolve()
    .then(() => userService.getSecretKey(api))
    .then((response) => {
      const secret = response && response.data && response.data.data;
      if (!secret) {
        return null;
      }
      return userService.getCloudToken(api, secret);
    })
    .then((response) => {
      const payload = response && response.data && response.data.data;
      const token = payload && payload.access_token;
      return writeToken(server, token);
    })
    .catch(() => {
      // Deliberately swallowed. See the null-resolve note above.
      return null;
    })
    .then((token) => {
      delete inFlight[api];
      return token;
    });

  inFlight[api] = attempt;

  return attempt;
}

/**
 * The token to send on the next request to this server: the stored one if
 * there is one, otherwise a freshly acquired one.
 *
 * This is the ordering guarantee. Awaiting it in the request interceptor means
 * no request leaves without a token unless acquisition itself failed.
 *
 * @param {{server_name: string, server_api: string}} server
 * @param {{userService?: Object}} [deps]
 * @returns {Promise<?string>}
 */
export function ensureToken(server, deps) {
  const existing = readToken(server);
  if (existing) {
    return Promise.resolve(existing);
  }
  return acquireToken(server, deps);
}

/**
 * Forget any in-flight acquisition. For tests only -- production code has no
 * reason to care.
 */
export function resetTokenAcquisition() {
  inFlight = {};
}

export default {
  serverStorageKey,
  readToken,
  writeToken,
  clearToken,
  acquireToken,
  ensureToken,
  resetTokenAcquisition,
};
